import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Award, Phone, Mail, Calendar, Clock, PoundSterling, CheckCircle,
  ArrowRight, FileText, HelpCircle, MapPin, Users
} from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import { PHONE_NUMBER, PHONE_TEL_HREF } from '../constants/contact';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';

const CompetitionPage = () => {
  return (
    <Layout>
      <div data-testid="competition-page">
        {/* Hero Section */}
        <section className="relative py-24 md:py-32 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://images.pexels.com/photos/34664777/pexels-photo-34664777.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
              alt="Heritage Red Telephone Box in countryside"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-cpre-new-yew/95 via-cpre-new-yew/80 to-cpre-new-yew/50" />
          </div>
          <div className="relative container-custom">
            <div className="max-w-3xl">
              <Badge className="bg-cpre-harvest-gold text-cpre-new-yew hover:bg-cpre-autumn-leaf hover:text-white mb-6 text-sm px-4 py-2" data-testid="hero-badge">
                <Award className="w-4 h-4 mr-2 inline" />
                Centenary Competition 2026
              </Badge>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold text-white mb-6 leading-tight" data-testid="hero-title">
                CPRE Rutland and Lincolnshire Centenary Year Competition – Restore Your Local Heritage Telephone Box
              </h1>
              <p className="text-xl text-white/90 leading-relaxed mb-8">
                Celebrating 100 years of supporting rural communities and their countryside by helping to rescue and preserve a national treasure.
              </p>
              <div className="flex flex-wrap gap-4">
                <a href="mailto:chair@cprerutlandandlincs.uk?subject=Telephone Box Competition Application">
                  <Button size="lg" className="bg-cpre-harvest-gold hover:bg-cpre-harvest-gold/90 text-cpre-new-yew rounded-full px-8 h-14 text-lg font-semibold" data-testid="hero-apply-btn">
                    <Mail className="w-5 h-5 mr-2" />
                    Apply Now
                  </Button>
                </a>
                <a href={PHONE_TEL_HREF}>
                  <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-cpre-new-yew rounded-full px-8 h-14 text-lg" data-testid="hero-call-btn">
                    <Phone className="w-5 h-5 mr-2" />
                    {PHONE_NUMBER}
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Key Information Cards */}
        <section className="py-12 bg-cpre-pebble/30" data-testid="key-info-section">
          <div className="container-custom">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <Card className="bg-white border-0 shadow-md">
                <CardContent className="p-6 text-center">
                  <div className="w-14 h-14 bg-cpre-harvest-gold rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <PoundSterling className="w-7 h-7 text-cpre-new-yew" />
                  </div>
                  <h3 className="font-heading font-bold text-xl text-cpre-new-yew mb-2">Up to £1k</h3>
                  <p className="text-muted-foreground text-sm">Funding per box</p>
                </CardContent>
              </Card>
              <Card className="bg-white border-0 shadow-md">
                <CardContent className="p-6 text-center">
                  <div className="w-14 h-14 bg-cpre-new-yew rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <Users className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="font-heading font-bold text-xl text-cpre-new-yew mb-2">Up to 5</h3>
                  <p className="text-muted-foreground text-sm">Projects supported</p>
                </CardContent>
              </Card>
              <Card className="bg-white border-0 shadow-md">
                <CardContent className="p-6 text-center">
                  <div className="w-14 h-14 bg-cpre-autumn-leaf rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <Calendar className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="font-heading font-bold text-xl text-cpre-new-yew mb-2">1 July 2026</h3>
                  <p className="text-muted-foreground text-sm">Closing date for entries</p>
                </CardContent>
              </Card>
              <Card className="bg-white border-0 shadow-md">
                <CardContent className="p-6 text-center">
                  <div className="w-14 h-14 bg-cpre-hill rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <Clock className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="font-heading font-bold text-xl text-cpre-new-yew mb-2">1 October 2026</h3>
                  <p className="text-muted-foreground text-sm">Completion deadline</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* About the Competition */}
        <section className="section-padding" data-testid="about-section">
          <div className="container-custom">
            <div className="max-w-4xl mx-auto">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-cpre-new-yew mb-8">
                About the Competition
              </h2>
              
              <div className="prose prose-lg max-w-none text-cpre-new-yew/80 space-y-6">
                <p>
                  Supported by a national grant, CPRE Rutland and Lincolnshire are to celebrate 100 years of supporting rural communities and their countryside by helping to rescue and preserve a national treasure, the heritage telephone box still to be found in many villages and market towns.
                </p>

                <div className="bg-cpre-pebble/30 rounded-2xl p-8 my-8">
                  <h3 className="font-heading font-bold text-lg text-cpre-new-yew mb-4">
                    Community Heartbeat Trust
                  </h3>
                  <p className="text-cpre-new-yew/80">
                    The project is being supported by the national life saving defibrillator charity the Community Heartbeat Trust which has over 9000 defibrillators and/or emergency phones in villages, market towns and cities around the UK, many of them housed in heritage telephone boxes adopted from BT.
                  </p>
                </div>

                <div className="bg-cpre-pebble/30 rounded-2xl p-8 my-8">
                  <h3 className="font-heading font-bold text-lg text-cpre-new-yew mb-4">
                    British Coating Federation
                  </h3>
                  <p className="text-cpre-new-yew/80">
                    The work of the charity is supported by the British Coating Federation who have for some years sponsored both telephone box paint restoration and life saving CPR training for local community groups and parish councils.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Community Questions */}
        <section className="section-padding bg-cpre-heritage-white" data-testid="questions-section">
          <div className="container-custom">
            <div className="max-w-4xl mx-auto">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-cpre-new-yew mb-4">
                Community Questions
              </h2>
              <p className="text-muted-foreground mb-8">
                Consider the following when applying:
              </p>

              <div className="space-y-6">
                <Card className="bg-white border border-stone-200">
                  <CardContent className="p-6">
                    <div className="flex gap-4">
                      <div className="w-10 h-10 bg-cpre-new-yew rounded-xl flex items-center justify-center flex-shrink-0">
                        <span className="text-white font-bold">1</span>
                      </div>
                      <div>
                        <h4 className="font-heading font-semibold text-lg text-cpre-new-yew">
                          Does your neighbourhood or parish council have a heritage telephone box in need of restoration?
                        </h4>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-white border border-stone-200">
                  <CardContent className="p-6">
                    <div className="flex gap-4">
                      <div className="w-10 h-10 bg-cpre-new-yew rounded-xl flex items-center justify-center flex-shrink-0">
                        <span className="text-white font-bold">2</span>
                      </div>
                      <div>
                        <h4 className="font-heading font-semibold text-lg text-cpre-new-yew">
                          Has it been adopted by your local community or would you like to do so if it could be satisfactorily restored?
                        </h4>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-white border border-stone-200">
                  <CardContent className="p-6">
                    <div className="flex gap-4">
                      <div className="w-10 h-10 bg-cpre-new-yew rounded-xl flex items-center justify-center flex-shrink-0">
                        <span className="text-white font-bold">3</span>
                      </div>
                      <div>
                        <h4 className="font-heading font-semibold text-lg text-cpre-new-yew">
                          Could your local box be repurposed in the community interest? If so, what innovative ideas for its use do you have?
                        </h4>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* Funding Support */}
        <section className="section-padding" data-testid="funding-section">
          <div className="container-custom">
            <div className="max-w-4xl mx-auto">
              <div className="bg-cpre-new-yew rounded-2xl p-8 md:p-12 text-center">
                <PoundSterling className="w-16 h-16 text-cpre-harvest-gold mx-auto mb-6" />
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-white mb-6">
                  Funding Support
                </h2>
                <p className="text-white/90 text-lg max-w-2xl mx-auto">
                  To mark its Centenary year CPRE plans to help restore up to five boxes in Rutland and Lincolnshire during 2026 with up to £1k funding for the renovation work on each box.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Important Dates */}
        <section className="section-padding bg-cpre-pebble/30" data-testid="dates-section">
          <div className="container-custom">
            <div className="max-w-4xl mx-auto">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-cpre-new-yew mb-8 text-center">
                Important Dates
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-2xl mx-auto">
                <Card className="bg-white border-2 border-cpre-autumn-leaf shadow-md">
                  <CardContent className="p-6 text-center">
                    <Calendar className="w-10 h-10 text-cpre-autumn-leaf mx-auto mb-4" />
                    <h3 className="font-heading font-bold text-xl text-cpre-new-yew mb-2">Closing Date</h3>
                    <p className="text-2xl font-bold text-cpre-autumn-leaf">1 July 2026</p>
                    <p className="text-muted-foreground text-sm mt-2">Closing date for entries</p>
                  </CardContent>
                </Card>
                <Card className="bg-white border-2 border-cpre-new-yew shadow-md">
                  <CardContent className="p-6 text-center">
                    <Clock className="w-10 h-10 text-cpre-new-yew mx-auto mb-4" />
                    <h3 className="font-heading font-bold text-xl text-cpre-new-yew mb-2">Completion Deadline</h3>
                    <p className="text-2xl font-bold text-cpre-new-yew">1 October 2026</p>
                    <p className="text-muted-foreground text-sm mt-2">Renovation work must be completed</p>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* Contact / CTA Section */}
        <section className="section-padding bg-cpre-new-yew" data-testid="contact-section">
          <div className="container-custom">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-white mb-4">
                Interested in such a project for your community?
              </h2>
              <p className="text-white/80 text-lg mb-8">
                Contact Ron Simpson BEM to discuss your project or submit your application.
              </p>
              
              <div className="flex flex-col sm:flex-row justify-center gap-4 mb-8">
                <a href="mailto:chair@cprerutlandandlincs.uk?subject=Telephone Box Competition Application">
                  <Button size="lg" className="bg-cpre-harvest-gold hover:bg-cpre-harvest-gold/90 text-cpre-new-yew rounded-full px-10 h-14 w-full sm:w-auto" data-testid="cta-email-btn">
                    <Mail className="w-5 h-5 mr-2" />
                    chair@cprerutlandandlincs.uk
                  </Button>
                </a>
                <a href={PHONE_TEL_HREF}>
                  <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-cpre-new-yew rounded-full px-10 h-14 w-full sm:w-auto" data-testid="cta-phone-btn">
                    <Phone className="w-5 h-5 mr-2" />
                    {PHONE_NUMBER}
                  </Button>
                </a>
              </div>

              <div className="bg-white/10 rounded-xl p-6 max-w-md mx-auto">
                <p className="text-white/60 text-sm">
                  Closing date for entries: <strong className="text-cpre-harvest-gold">1 July 2026</strong>
                </p>
                <p className="text-white/60 text-sm mt-1">
                  Renovation work must be completed by: <strong className="text-cpre-harvest-gold">1 October 2026</strong>
                </p>
              </div>
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
};

export default CompetitionPage;
